"use client";

import {
  Children,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from "react";
import {
  arrowsShown,
  carouselAttributes,
  carouselCycle,
  carouselLabels,
  carouselMode,
  carouselPositions,
  carouselStep,
  carouselStyle,
  swipeDirection,
  type CarouselDevice,
  type CarouselNoun,
  type CarouselSettings,
} from "./carousel-settings";
import {
  carouselKeyframes,
  kenBurnsDuration,
  kenBurnsKeyframes,
} from "./carousel-effects";
import { animateScroll } from "./carousel-scroll";
import { motionStill } from "@/utils/motion";
import "./carousel.css";

const DESKTOP = "(min-width: 768px)";
const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
const useClientLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;
const INTERACTIVE = "a,button,input,select,textarea,[contenteditable=true]";

type Direction = 1 | -1;

function Icon({ name }: { name: "prev" | "next" | "pause" | "play" }) {
  const paths = {
    prev: <polyline points="15 18 9 12 15 6" />,
    next: <polyline points="9 18 15 12 9 6" />,
    pause: (
      <>
        <rect x="6" y="5" width="4" height="14" rx="1" />
        <rect x="14" y="5" width="4" height="14" rx="1" />
      </>
    ),
    play: <polygon points="7 5 19 12 7 19 7 5" />,
  };
  const filled = name === "pause" || name === "play";
  return (
    <svg
      className="re-carousel-icon"
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke={filled ? "none" : "currentColor"}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {paths[name]}
    </svg>
  );
}
/** Firkantet's arrows have a shaft. */
function ArrowIcon({ dir }: { dir: "prev" | "next" }) {
  return (
    <svg
      className="re-carousel-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {dir === "prev" ? (
        <>
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </>
      ) : (
        <>
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </>
      )}
    </svg>
  );
}

export interface CarouselProps {
  settings: CarouselSettings;
  /** the region's name: the section heading, else the type's noun (»Citater«, »Billeder«) */
  label: string;
  /** names the controls: »Forrige citat«, »Vis citat 2« */
  noun: CarouselNoun;
  /** a slide's aria-roledescription */
  slideName?: string;
  /** each slide's own name, added to »n af m« and to its dot's name */
  slideLabels?: string[];
  /** the colour of the controls lying on the slides (sides, inside, corner) */
  overlayTone?: "light" | "dark";
  /** arrows on the sides appear on hover and focus only (always on touch screens) */
  revealArrows?: "always" | "hover";
  /** the carousel and each slide take the height they are given */
  fill?: boolean;
  /** the carousel is a look-system reveal of its own (src/styles/motion.css), and its slides move with it */
  reveal?: boolean;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}

/**
 * The shared carousel. The slides are rendered on the server; the first render (and the server's) is the first slide,
 * not changing by itself, with a polite live region. After hydration it reads the device and reduced motion and
 * starts when it should: it pauses on hover and focus, while the tab is hidden, while less than half of it is on
 * screen, while a video in it plays and while a cached CMS preview is hidden, and stops for good when the visitor
 * navigates. One slide at a time is a stack animated with the Web Animations API; several per view (or a peek) are a
 * native scroll-snap strip.
 */
export default function Carousel({
  settings: s,
  label,
  noun,
  slideName = "dias",
  slideLabels,
  overlayTone,
  revealArrows,
  fill,
  reveal,
  className,
  style,
  children,
}: CarouselProps) {
  const id = useId();
  const trackId = `${id}track`;
  const slides = Children.toArray(children);
  const count = slides.length;
  const labels = carouselLabels(noun);
  const rootRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [device, setDevice] = useState<CarouselDevice | null>(null);
  const [index, setIndex] = useState(0);
  const [visitorPlaying, setVisitorPlaying] = useState<boolean>();
  const [reducedMotion, setReducedMotion] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [inView, setInView] = useState(true);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [previewActive, setPreviewActive] = useState(true);
  const [status, setStatus] = useState("");
  const direction = useRef<Direction>(1);
  const dragOffset = useRef<number | null>(null);
  const stopScroll = useRef<() => void>(() => {});
  const gliding = useRef(false);

  const perView = { mobile: s.columns[0], desktop: s.columns[1] };
  const positionsOn = (d: CarouselDevice) =>
    carouselPositions(count, carouselMode(s, d) === "track" ? perView[d] : 1);
  const positionsMobile = positionsOn("mobile");
  const positionsDesktop = positionsOn("desktop");
  const still = positionsMobile <= 1 && positionsDesktop <= 1;
  const positions = device
    ? positionsOn(device)
    : Math.max(positionsMobile, positionsDesktop);
  const mode = device ? carouselMode(s, device) : null;
  const visibleCount = device
    ? mode === "track"
      ? perView[device]
      : 1
    : Math.min(
        ...(["mobile", "desktop"] as const).map((d) =>
          carouselMode(s, d) === "track" ? perView[d] : 1,
        ),
      );
  const current = Math.min(index, positions - 1);
  const intent = s.autoplay && !still && (visitorPlaying ?? !reducedMotion);
  const atEnd = !s.loop && current >= positions - 1;
  const playing =
    device !== null &&
    intent &&
    positions > 1 &&
    !hovered &&
    !focused &&
    !hidden &&
    inView &&
    !videoPlaying &&
    previewActive &&
    !atEnd;
  const showArrows = !still && arrowsShown(s, !intent);
  const pauseButton = s.autoplay && !still;
  const pillPlace = s.dotPosition !== "below";
  const dotsInPill = s.showDots && pillPlace;
  const pauseInPill = pauseButton && pillPlace;
  const bothTracks =
    carouselMode(s, "mobile") === "track" &&
    carouselMode(s, "desktop") === "track";
  const live =
    (mode ?? (bothTracks ? "track" : "stack")) === "track"
      ? "off"
      : playing
        ? "off"
        : "polite";

  const slideElements = () =>
    [...(trackRef.current?.children ?? [])] as HTMLElement[];

  const scrollToPosition = useCallback(
    (position: number, instant = false) => {
      const track = trackRef.current;
      const elements = [...(track?.children ?? [])] as HTMLElement[];
      if (!track || !elements[position]) return;
      const left = Math.min(
        elements[position].offsetLeft - elements[0].offsetLeft,
        track.scrollWidth - track.clientWidth,
      );
      stopScroll.current();
      const reduce = instant || window.matchMedia(REDUCED_MOTION).matches;
      gliding.current = true;
      stopScroll.current = animateScroll(
        track,
        left,
        reduce ? 0 : s.effectDuration,
        () => {
          gliding.current = false;
        },
      );
    },
    [s.effectDuration],
  );

  const go = (target: number, d: Direction, manual: boolean) => {
    if (target === current || target < 0 || target >= positions) return;
    direction.current = d;
    if (manual) {
      setVisitorPlaying(false);
      if (mode === "track")
        setStatus(
          labels.status(
            target + 1,
            Math.min(target + visibleCount, count),
            count,
          ),
        );
    }
    setIndex(target);
    if (mode === "track") scrollToPosition(target);
  };
  const step = (by: Direction, manual: boolean) => {
    const target = carouselStep(current, by, positions, s.loop);
    if (target !== null) go(target, by, manual);
  };
  const advance = useRef(() => {});
  advance.current = () => step(1, false);

  useEffect(() => {
    const desktop = window.matchMedia(DESKTOP);
    const motion = window.matchMedia(REDUCED_MOTION);
    const read = () => {
      setDevice(desktop.matches ? "desktop" : "mobile");
      setReducedMotion(motion.matches);
    };
    const visibility = () => setHidden(document.hidden);
    read();
    visibility();
    desktop.addEventListener("change", read);
    motion.addEventListener("change", read);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      desktop.removeEventListener("change", read);
      motion.removeEventListener("change", read);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const videos = () =>
      setVideoPlaying(
        [...root.querySelectorAll("video")].some(
          (video) => !video.paused && !video.ended,
        ),
      );
    const preview = (event: Event) =>
      setPreviewActive(
        (event as CustomEvent<{ active: boolean }>).detail?.active !== false,
      );
    for (const type of ["play", "playing", "pause", "ended"])
      root.addEventListener(type, videos, true);
    root.addEventListener("re-carousel-playback", preview);
    const observer =
      typeof IntersectionObserver === "undefined"
        ? undefined
        : new IntersectionObserver(
            ([entry]) =>
              setInView(
                entry.intersectionRatio >= 0.5 ||
                  entry.intersectionRect.height >= window.innerHeight * 0.5,
              ),
            { threshold: [0, 0.25, 0.5, 0.75, 1] },
          );
    observer?.observe(root);
    return () => {
      for (const type of ["play", "playing", "pause", "ended"])
        root.removeEventListener(type, videos, true);
      root.removeEventListener("re-carousel-playback", preview);
      observer?.disconnect();
    };
  }, []);

  // A device change keeps the position where it still exists and puts a strip back at it without gliding.
  useEffect(() => {
    if (!device) return;
    if (index > positions - 1) setIndex(positions - 1);
    for (const slide of slideElements()) {
      slide.style.transform = "";
      slide.style.visibility = "";
    }
    if (mode === "track")
      scrollToPosition(Math.min(index, positions - 1), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on a device or layout change
  }, [device, mode, positions]);

  const remaining = useRef<number | null>(null);
  const timedIndex = useRef(current);
  useEffect(() => {
    if (timedIndex.current !== current) {
      timedIndex.current = current;
      remaining.current = null;
    }
    if (!playing) return;
    const delay = remaining.current ?? carouselCycle(s, reducedMotion);
    const began = performance.now();
    const timer = window.setTimeout(() => {
      remaining.current = null;
      advance.current();
    }, delay);
    return () => {
      window.clearTimeout(timer);
      remaining.current = Math.max(0, delay - (performance.now() - began));
    };
  }, [playing, current, s, reducedMotion]);

  // Runs before paint, so the new slide never flashes before its animation. Finishing every running change first
  // keeps fast clicks from leaving a third slide visible.
  const shown = useRef(current);
  useClientLayoutEffect(() => {
    const from = shown.current;
    shown.current = current;
    const offset = dragOffset.current;
    dragOffset.current = null;
    const elements = slideElements();
    for (const slide of elements) {
      slide.style.transform = "";
      slide.style.visibility = "";
    }
    if (from === current || mode !== "stack") return;
    for (const slide of elements)
      for (const animation of slide.getAnimations({ subtree: true }))
        if (animation.id !== "re-carousel-ken-burns") animation.finish();
    if (reducedMotion || s.effect === "none") return;
    const incoming = elements[current];
    const outgoing = elements[from];
    if (!incoming || !outgoing) return;
    const d = direction.current;
    // A slide may mark several pictures or texts, of which a breakpoint shows one (Fokusbånd's classic box and strip).
    const marked = (slide: HTMLElement, part: "media" | "copy") => [
      ...slide.querySelectorAll<HTMLElement>(`[data-carousel-${part}]`),
    ];
    const media = marked(incoming, "media");
    const copy = marked(incoming, "copy");
    const width = viewportRef.current?.clientWidth ?? 0;
    const frames = carouselKeyframes(s.effect, d, {
      width,
      media: media.length > 0,
      copy: copy.length > 0,
      origin: media.length ? focusOrigin(media[0]) : undefined,
    });
    const dragged =
      offset !== null && (s.effect === "slide" || s.effect === "parallax");
    const left =
      dragged && width ? 1 - Math.min(1, Math.abs(offset) / width) : 1;
    const timing = {
      duration: Math.max(1, s.effectDuration * left),
      easing: dragged ? "ease-out" : "ease",
    };
    const slideFrames = dragged
      ? {
          incoming: [
            { transform: `translateX(calc(${d * 100}% + ${offset}px))` },
            { transform: "none" },
          ],
          outgoing: [
            { transform: `translateX(${offset}px)`, visibility: "visible" },
            { transform: `translateX(${-d * 100}%)`, visibility: "visible" },
          ],
        }
      : frames.slide;
    if (slideFrames.incoming.length)
      incoming.animate(slideFrames.incoming, timing);
    if (slideFrames.outgoing.length)
      outgoing.animate(slideFrames.outgoing, timing);
    const play = (elements: HTMLElement[], keyframes: Keyframe[]) => {
      if (keyframes.length)
        for (const element of elements) element.animate(keyframes, timing);
    };
    if (frames.media) {
      play(media, frames.media.incoming);
      play(marked(outgoing, "media"), frames.media.outgoing);
    }
    if (frames.copy) {
      play(copy, frames.copy.incoming);
      play(marked(outgoing, "copy"), frames.copy.outgoing);
    }
  }, [current, mode]);

  // Ken Burns moves the current slide's picture for as long as it is seen, pausing with the automatic change.
  const kenBurns = useRef<Animation | null>(null);
  useEffect(() => {
    if (
      s.effect !== "kenBurns" ||
      reducedMotion ||
      device === null ||
      motionStill(rootRef.current)
    )
      return;
    const media = slideElements()[current]?.querySelector<HTMLElement>(
      "[data-carousel-media]",
    );
    if (!media) return;
    const animation = media.animate(
      kenBurnsKeyframes(current, focusOrigin(media)),
      {
        duration: kenBurnsDuration(s.effectDuration, s.interval, intent),
        easing: "linear",
        fill: "both",
        id: "re-carousel-ken-burns",
      },
    );
    kenBurns.current = animation;
    const outgoing = animation;
    return () => {
      // The outgoing picture keeps moving through its fade, then lets go.
      window.setTimeout(() => outgoing.cancel(), s.effectDuration + 50);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a new move per slide and setting
  }, [current, s.effect, s.effectDuration, s.interval, reducedMotion, device]);
  useEffect(() => {
    const animation = kenBurns.current;
    if (!animation || !intent) return;
    if (playing) animation.play();
    else animation.pause();
  }, [playing, intent]);

  const onScroll = () => {
    const track = trackRef.current;
    if (mode !== "track" || !track || gliding.current) return;
    const elements = slideElements();
    const end = track.scrollWidth - track.clientWidth;
    const nearest =
      track.scrollLeft >= end - 2
        ? positions - 1
        : elements.reduce(
            (best, slide, i) =>
              Math.abs(
                slide.offsetLeft - elements[0].offsetLeft - track.scrollLeft,
              ) <
              Math.abs(
                elements[best].offsetLeft -
                  elements[0].offsetLeft -
                  track.scrollLeft,
              )
                ? i
                : best,
            0,
          );
    const position = Math.min(nearest, positions - 1);
    if (position !== current) setIndex(position);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      event.target !== event.currentTarget &&
      (event.target as HTMLElement).closest(INTERACTIVE)
    )
      return;
    if (positions <= 1) return;
    const key = event.key;
    if (key === "ArrowLeft") step(-1, true);
    else if (key === "ArrowRight") step(1, true);
    else if (key === "Home") go(0, -1, true);
    else if (key === "End") go(positions - 1, 1, true);
    else return;
    event.preventDefault();
  };

  const drag = useRef<{
    id: number;
    x: number;
    y: number;
    dx: number;
    dy: number;
    axis?: "x" | "y";
  } | null>(null);
  const follows = s.effect === "slide" || s.effect === "parallax";
  const neighbour = (dx: number) =>
    carouselStep(current, dx < 0 ? 1 : -1, positions, s.loop);
  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse" || mode !== "stack" || positions <= 1)
      return;
    drag.current = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      dx: 0,
      dy: 0,
    };
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const state = drag.current;
    if (!state || state.id !== event.pointerId) return;
    state.dx = event.clientX - state.x;
    state.dy = event.clientY - state.y;
    if (!state.axis) {
      if (Math.abs(state.dx) < 8 && Math.abs(state.dy) < 8) return;
      state.axis = Math.abs(state.dx) > Math.abs(state.dy) ? "x" : "y";
      if (state.axis === "x")
        event.currentTarget.setPointerCapture?.(event.pointerId);
    }
    if (state.axis !== "x" || !follows || reducedMotion) return;
    const elements = slideElements();
    const target = neighbour(state.dx);
    const dx = target === null ? state.dx / 3 : state.dx;
    elements[current].style.transform = `translateX(${dx}px)`;
    for (const [i, slide] of elements.entries())
      if (i !== current) {
        const near = i === target;
        slide.style.transform = near
          ? `translateX(calc(${state.dx < 0 ? 100 : -100}% + ${dx}px))`
          : "";
        slide.style.visibility = near ? "visible" : "";
      }
  };
  const onPointerEnd = (event: PointerEvent<HTMLDivElement>) => {
    const state = drag.current;
    if (!state || state.id !== event.pointerId) return;
    drag.current = null;
    if (state.axis !== "x") return;
    const d = swipeDirection(state.dx, state.dy);
    const target = d ? carouselStep(current, d, positions, s.loop) : null;
    if (d && target !== null) {
      dragOffset.current = state.dx;
      go(target, d, true);
      return;
    }
    const elements = slideElements();
    const settle = { duration: reducedMotion ? 0 : 250, easing: "ease-out" };
    for (const slide of elements) {
      const moved = slide.style.transform;
      if (!moved) continue;
      const back =
        slide === elements[current]
          ? "none"
          : `translateX(${state.dx < 0 ? 100 : -100}%)`;
      slide
        .animate([{ transform: moved }, { transform: back }], settle)
        .finished.catch(() => {})
        .finally(() => {
          slide.style.transform = "";
          slide.style.visibility = "";
        });
    }
  };

  const dotOnly = (i: number) =>
    i >= positionsDesktop
      ? { "data-only": "mobile" }
      : i >= positionsMobile
        ? { "data-only": "desktop" }
        : {};
  const dotCount = Math.max(positionsMobile, positionsDesktop);
  const dots =
    s.dotStyle === "counter" ? (
      <span className="re-carousel-counter" aria-hidden="true">
        {current + 1} / {count}
      </span>
    ) : (
      <div className="re-carousel-dots" role="group" aria-label={labels.dots}>
        {Array.from({ length: dotCount }, (_, i) => (
          <button
            type="button"
            key={i}
            className="re-carousel-dot"
            aria-label={labels.dot(i + 1, slideLabels?.[i])}
            aria-current={i === current}
            aria-controls={trackId}
            {...dotOnly(i)}
            onClick={() => go(i, i > current ? 1 : -1, true)}
          />
        ))}
      </div>
    );
  const arrow = (dir: "prev" | "next", extra: Record<string, string> = {}) => (
    <button
      type="button"
      className="re-carousel-arrow"
      data-dir={dir}
      key={`${dir}-${extra["data-place"] ?? extra["data-only"] ?? "row"}`}
      aria-label={dir === "prev" ? labels.previous : labels.next}
      aria-controls={trackId}
      {...extra}
      disabled={
        !s.loop && (dir === "prev" ? current === 0 : current >= positions - 1)
      }
      onClick={() => step(dir === "prev" ? -1 : 1, true)}
    >
      {s.arrowStyle === "square" ? (
        <ArrowIcon dir={dir} />
      ) : (
        <Icon name={dir} />
      )}
    </button>
  );
  const pause = pauseButton ? (
    <button
      type="button"
      className="re-carousel-pause"
      aria-controls={trackId}
      aria-label={intent ? labels.pause : labels.play}
      onClick={() => setVisitorPlaying(!intent)}
    >
      <Icon name={intent ? "pause" : "play"} />
    </button>
  ) : null;
  const counterBetween = s.counterBetweenArrows && !s.showDots;
  const rowArrows =
    showArrows &&
    (s.arrowPosition === "below" || s.arrowPosition === "outside");
  const rowDots = s.showDots && s.dotPosition === "below" && !still;
  const outsideOnly: Record<string, string> =
    s.arrowPosition === "outside" ? { "data-only": "mobile" } : {};
  const row =
    rowArrows || rowDots ? (
      <div className="re-carousel-controls">
        {rowArrows && arrow("prev", outsideOnly)}
        {rowDots && dots}
        {rowArrows && counterBetween && (
          <span className="re-carousel-counter" aria-hidden="true">
            {current + 1} / {count}
          </span>
        )}
        {rowArrows && arrow("next", outsideOnly)}
      </div>
    ) : null;

  if (!count) return null;
  return (
    <div
      ref={rootRef}
      className={["re-carousel", className].filter(Boolean).join(" ")}
      role="region"
      aria-roledescription="karrusel"
      aria-label={label}
      {...carouselAttributes(s)}
      {...(positionsMobile <= 1 ? { "data-static-mobile": "" } : {})}
      {...(positionsDesktop <= 1 ? { "data-static-desktop": "" } : {})}
      {...(overlayTone ? { "data-overlay-tone": overlayTone } : {})}
      {...(revealArrows === "hover" ? { "data-reveal-arrows": "hover" } : {})}
      {...(fill ? { "data-fill": "" } : {})}
      {...(reveal ? { "data-re-reveal": "" } : {})}
      data-intent={intent}
      data-playing={playing}
      style={{ ...carouselStyle(s), ...style } as CSSProperties}
      onFocusCapture={(event) => {
        if (!(event.target as HTMLElement).closest(".re-carousel-pause"))
          setFocused(true);
      }}
      onBlurCapture={(event) => {
        const next = event.relatedTarget as Node | null;
        if (
          !next ||
          !event.currentTarget.contains(next) ||
          (next as HTMLElement).closest?.(".re-carousel-pause")
        )
          setFocused(false);
      }}
    >
      {!pauseInPill && pause}
      <div className="re-carousel-stage">
        {(dotsInPill || pauseInPill) && !still && (
          <div className="re-carousel-pill" data-place={s.dotPosition}>
            {pause}
            {dotsInPill && dots}
          </div>
        )}
        <div
          ref={viewportRef}
          className="re-carousel-viewport"
          tabIndex={0}
          aria-label={label}
          onKeyDown={onKeyDown}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
        >
          <div
            ref={trackRef}
            className="re-carousel-track"
            id={trackId}
            aria-live={live}
            aria-atomic="false"
            onScroll={onScroll}
          >
            {slides.map((slide, i) => {
              const visible = i >= current && i < current + visibleCount;
              return (
                <div
                  key={i}
                  className="re-carousel-slide"
                  role="group"
                  aria-roledescription={slideName}
                  aria-label={labels.slide(i + 1, count, slideLabels?.[i])}
                  data-active={visible}
                  {...(visible ? {} : { inert: true })}
                >
                  {slide}
                </div>
              );
            })}
          </div>
        </div>
        {showArrows &&
          (s.arrowPosition === "sides" || s.arrowPosition === "outside") && (
            <>
              {arrow("prev", {
                "data-place": s.arrowPosition,
                ...(s.arrowPosition === "outside"
                  ? { "data-only": "desktop" }
                  : {}),
              })}
              {arrow("next", {
                "data-place": s.arrowPosition,
                ...(s.arrowPosition === "outside"
                  ? { "data-only": "desktop" }
                  : {}),
              })}
            </>
          )}
      </div>
      {row}
      <p className="re-carousel-status" aria-live="polite">
        {status}
      </p>
    </div>
  );
}

/** The picture's focus point (its object-position) as the origin of Zoom and Ken Burns. */
function focusOrigin(media: HTMLElement): string {
  const image = media.matches("img,video")
    ? media
    : media.querySelector("img,video");
  const position = image ? getComputedStyle(image).objectPosition : "";
  return position && position !== "50% 50%" ? position : "center";
}
